import { Logger } from '~~/helpers/logger';

import { LAST_SEEN_PRODUCTS } from '~~/constants/localStorage';
import { PRODUCT_ATTRIBUTES } from '~~/constants/products';
import { MILLISECONDS_IN_MONTH } from '~~/constants/timeUnits';
import { getCategoriesStringFromProduct } from '~~/helpers/category/getCategoryStringFromProduct';
import { getDiscountedPrice, getFullProductPrice } from '~~/helpers/product/getPriceInfo';
import { getProductAttributesExtended } from '~~/helpers/product/getProductAttributesExtended';
import { addNativeRoutingToProductUrl } from '~~/helpers/routes/nativeRouting/transformProductsForNativeRouting';
import type { LastSeenProduct } from '~~/types/product/LastSeenProduct';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { productGetters } from '~/utils/ct';

const MAX_PRODUCTS = 10;

export const useLastSeenProducts = () => {

  const { countryCode, languageAndCountry, currencyCode } = useLocalization();

  const localStorageKey = computed(() => `${LAST_SEEN_PRODUCTS}_${languageAndCountry.value}`);

  const lastSeenProducts = useState<LastSeenProduct[]>('lastSeenProducts', () => []);

  function getLastSeenFromLocalStorage(): LastSeenProduct[] {

    try {

      const savedProducts = localStorage.getItem(localStorageKey.value);

      if (savedProducts) {

        const parsedProducts = JSON.parse(savedProducts);

        return parsedProducts;
      
      }
    
    } catch (error) {

      Logger.error('Failed to parse last seen products from localStorage:', error);
    
    }

    return [];
  
  }

  function saveLastSeenToLocalStorage(products: LastSeenProduct[]) {

    try {

      localStorage.setItem(localStorageKey.value, JSON.stringify(products));
    
    } catch (error) {

      Logger.error('Failed to save last seen products to localStorage:', error);
    
    }
  
  }

  function getUpToDateProducts(lastSeenProducts: LastSeenProduct[], currentTime: number): LastSeenProduct[] {

    return lastSeenProducts.filter(product => {

      return (currentTime - product.timestamp) <= MILLISECONDS_IN_MONTH;
    
    });
  
  }

  function removeDuplicates(lastSeenProducts: LastSeenProduct[], currentProduct: LastSeenProduct) {

    const existingIndex = lastSeenProducts.findIndex(p => p.sku === currentProduct.sku);

    if (existingIndex !== -1) {

      lastSeenProducts.splice(existingIndex, 1);
    
    }
  
  }

  function limitAmountOfProducts(lastSeenProducts: LastSeenProduct[]) {

    if (lastSeenProducts.length > MAX_PRODUCTS) {

      lastSeenProducts.splice(MAX_PRODUCTS);
    
    }
  
  }

  function transformProductIntoLastSeenProduct(
    product: ProductVariantExtended, currentTime: number,
  ): LastSeenProduct | null {

    const price = getFullProductPrice(product);

    if (!product._slug || !product._name || !product.sku || price === null) {

      return null;

    }

    const productLink = addNativeRoutingToProductUrl(product._slug, countryCode.value);
    const productImage = productGetters.getCoverImage(product);
    const extractedAttributes = getProductAttributesExtended(product,
      {
        attributeNames: [PRODUCT_ATTRIBUTES.BRAND],
        languageAndCountry: languageAndCountry.value,
      });

    const categories = getCategoriesStringFromProduct(product);

    return {
      sku: product.sku,
      name: product._name,
      link: productLink,
      image: productImage,
      timestamp: currentTime,
      brand: extractedAttributes.brand,
      price,
      discountedPrice: getDiscountedPrice(product),
      currency: currencyCode.value,
      categories,
    };
  
  }

  const addProductToLastSeen = (product: ProductVariantExtended) => {

    if (import.meta.server || !product) {

      return;

    }

    const currentTime = Date.now();
    const lastSeenFromStorage = getLastSeenFromLocalStorage();
    const lastSeenProduct = transformProductIntoLastSeenProduct(product, currentTime);

    if (!lastSeenProduct) {

      return;

    }

    const validProducts = getUpToDateProducts(lastSeenFromStorage, currentTime);
    removeDuplicates(validProducts, lastSeenProduct);
    limitAmountOfProducts(validProducts);

    lastSeenProducts.value = validProducts;

    saveLastSeenToLocalStorage([lastSeenProduct, ...validProducts]);
  
  };

  return {
    lastSeenProducts,
    addProductToLastSeen,
  };

};
